import { CKEditor } from 'ckeditor4-react';
import React, { useEffect, useRef, useState, useMemo } from 'react';

const editorConfig = {
  toolbar: [
    { name: 'basicstyles', items: ['CopyFormatting', 'RemoveFormat'] },
    {
      name: 'clipboard',
      items: ['Cut', 'Copy', 'Paste', 'SelectAll', '-', 'Undo', 'Redo', '-'],
    },
    { name: 'editing', items: ['Find', 'Replace', '-'] },
    {
      name: 'basicstyles',
      items: [
        'Bold',
        'Italic',
        'Underline',
        'Strike',
        'Subscript',
        'Superscript',
        '-',
      ],
    },
    {
      name: 'paragraph',
      items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'],
    },
    { name: 'insert', items: ['HorizontalRule', 'SpecialChar'] },
    { name: 'document', items: ['Source'] },
    { name: 'links', items: ['Link', 'Unlink', '-'] },
    { name: 'insert', items: ['Table', '-'] },
    {
      name: 'paragraph',
      items: [
        'JustifyLeft',
        'JustifyCenter',
        'JustifyRight',
        'JustifyBlock',
        '-',
      ],
    },
    { name: 'colors', items: ['TextColor', 'BGColor', '-'] },
    { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
    { name: 'mytools', items: ['MyCustomButton'] },
  ],
  fillEmptyBlocks: false,
  versionCheck: false,
};

const CkEditor = ({ enableCustomButton, ...props }) => {
  const editorRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  useEffect(
    function registerPlugin() {
      if (!enableCustomButton || !window.CKEDITOR) return;
      if (window.CKEDITOR.plugins.get('mycustomplugin')) return;

      window.CKEDITOR.plugins.add('mycustomplugin', {
        init: function (editor) {
          editor.addCommand('toggleLoop', {
            exec: function (ed) {
              const sel = ed.getSelection();
              if (!sel) return;

              const ranges = sel.getRanges();
              if (!ranges || !ranges.length) return;

              // Allowed block types (non-table)
              const blockNames = [
                'p',
                'li',
                'div',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
              ];
              // Collect unique selected blocks (CKEDITOR.dom.element)
              const selectedBlocks = [];

              function pushUnique(node) {
                if (!node) return;
                // avoid duplicates by CKEditor equals
                const already = selectedBlocks.some(function (n) {
                  try {
                    return n.equals(node);
                  } catch (e) {
                    return false;
                  }
                });
                if (!already) selectedBlocks.push(node);
              }

              // Collect nodes (include the block that contains range.startContainer)
              ranges.forEach(function (range) {
                try {
                  const startEl = range.startContainer.getAscendant(function (
                    n
                  ) {
                    return (
                      n &&
                      n.type === window.CKEDITOR.NODE_ELEMENT &&
                      n.getName &&
                      (blockNames.indexOf(n.getName()) !== -1 ||
                        n.getName() === 'tr')
                    );
                  }, true);
                  pushUnique(startEl);
                } catch (e) {
                  // ignore
                }

                const walker = new window.CKEDITOR.dom.walker(range);
                walker.evaluator = function (node) {
                  return (
                    node &&
                    node.type === window.CKEDITOR.NODE_ELEMENT &&
                    node.getName &&
                    (blockNames.indexOf(node.getName()) !== -1 ||
                      node.getName() === 'tr')
                  );
                };

                let block;
                while ((block = walker.next())) {
                  pushUnique(block);
                }
              });

              if (selectedBlocks.length === 0) return;

              // Separate table rows and non-table blocks
              const tableNames = ['table', 'tr', 'td', 'th', 'tbody', 'thead'];
              const tableRows = selectedBlocks.filter(function (b) {
                try {
                  return b.getName && b.getName() === 'tr';
                } catch (e) {
                  return false;
                }
              });
              const nonTableBlocks = selectedBlocks.filter(function (b) {
                try {
                  return !(b.getName && b.getName() === 'tr');
                } catch (e) {
                  return true;
                }
              });

              // Detect if any of these are already looped
              var alreadyLooped = false;
              tableRows.forEach(function (b) {
                try {
                  if (b.hasAttribute && b.hasAttribute('data-loop'))
                    alreadyLooped = true;
                } catch (e) {}
              });

              // For non-table: check if they are inside a single wrapper div[data-loop]
              var wrapperDiv = null;
              if (nonTableBlocks.length > 0) {
                // find nearest ancestor with data-loop for the first block
                var anc = nonTableBlocks[0].getParent();
                while (anc && anc.getName && anc.getName() !== 'body') {
                  try {
                    if (
                      anc.getName() === 'div' &&
                      anc.hasAttribute &&
                      anc.hasAttribute('data-loop')
                    ) {
                      wrapperDiv = anc;
                      break;
                    }
                  } catch (e) {}
                  anc = anc.getParent();
                }
                if (wrapperDiv) {
                  // ensure wrapperDiv is common ancestor for all nonTableBlocks
                  var containsAll = nonTableBlocks.every(function (b) {
                    try {
                      return (
                        wrapperDiv.contains && wrapperDiv.contains(b, true)
                      );
                    } catch (e) {
                      return false;
                    }
                  });
                  if (containsAll) alreadyLooped = true;
                  else wrapperDiv = null;
                }
              }

              // Toggle behavior
              if (alreadyLooped) {
                // REMOVE loop from table rows
                tableRows.forEach(function (b) {
                  try {
                    if (b.removeAttribute) b.removeAttribute('data-loop');
                    // if (b.removeClass) b.removeClass("loop-marker");
                  } catch (e) {}
                });

                // UNWRAP non-table wrapperDiv (move children out and remove wrapper)
                if (wrapperDiv) {
                  try {
                    var parentElem = wrapperDiv.getParent();
                    while (wrapperDiv.getFirst()) {
                      if (parentElem && parentElem.insertBefore) {
                        parentElem.insertBefore(
                          wrapperDiv.getFirst(),
                          wrapperDiv
                        );
                      } else {
                        // fallback remove child
                        wrapperDiv.getFirst().remove();
                      }
                    }
                    wrapperDiv.remove();
                  } catch (e) {
                    // fallback safe removal: try native DOM
                    try {
                      var wNative = wrapperDiv.$;
                      var pNative = wNative.parentNode;
                      while (wNative.firstChild) {
                        pNative.insertBefore(wNative.firstChild, wNative);
                      }
                      pNative.removeChild(wNative);
                    } catch (ee) {}
                  }
                }

                ed.showNotification('Loop removed', 'success');
                return;
              }

              // APPLY loop:
              // 1) set data-loop on table rows
              tableRows.forEach(function (b) {
                try {
                  if (b.setAttribute) b.setAttribute('data-loop', 'looping');
                  // if (b.addClass) b.addClass("loop-marker");
                } catch (e) {}
              });

              // 2) wrap non-table blocks into a single wrapper
              if (nonTableBlocks.length > 0) {
                // ensure unique and sorted by document order
                var uniq = [];
                nonTableBlocks.forEach(function (b) {
                  var exists = uniq.some(function (x) {
                    try {
                      return x.equals(b);
                    } catch (e) {
                      return false;
                    }
                  });
                  if (!exists) uniq.push(b);
                });
                // sort by address (stable)
                uniq.sort(function (a, b) {
                  try {
                    var aa = a.getAddress ? a.getAddress().join() : '';
                    var bb = b.getAddress ? b.getAddress().join() : '';
                    return aa > bb ? 1 : -1;
                  } catch (e) {
                    return 0;
                  }
                });

                var firstBlock = uniq[0];
                var lastBlock = uniq[uniq.length - 1];

                // Native-safe wrapper creation & insertion in editor's iframe document
                try {
                  var nativeDoc =
                    ed.document && ed.document.$ ? ed.document.$ : document;
                  var wrapperNative = nativeDoc.createElement('div');
                  wrapperNative.setAttribute('data-loop', 'looping');
                  wrapperNative.className = 'loop-marker';

                  // Insert wrapperNative before the native firstBlock element
                  var firstNative = firstBlock.$;
                  var parentNative = firstNative.parentNode;
                  if (!parentNative) {
                    // fallback: insert at editable root
                    var root = ed.document.getBody().$;
                    root.appendChild(wrapperNative);
                  } else {
                    parentNative.insertBefore(wrapperNative, firstNative);
                  }

                  // Move native nodes into wrapperNative until after lastBlock
                  var afterLastNative = lastBlock.getNext
                    ? lastBlock.getNext()
                      ? lastBlock.getNext().$
                      : null
                    : lastBlock.$
                      ? lastBlock.$.nextSibling
                      : null;
                  // Start moving nodes from firstNative
                  var nodeNative = firstNative;
                  while (nodeNative && nodeNative !== afterLastNative) {
                    var nextNative = nodeNative.nextSibling;
                    wrapperNative.appendChild(nodeNative);
                    nodeNative = nextNative;
                  }
                } catch (e) {
                  // If native approach fails, fallback to CKEditor DOM approach (best-effort)
                  try {
                    var wrapper = new window.CKEDITOR.dom.element('div');
                    wrapper.setAttribute('data-loop', 'looping');
                    wrapper.addClass('loop-marker');
                    // insert wrapper before firstBlock via CKEditor API
                    try {
                      wrapper.insertBefore(firstBlock);
                    } catch (e2) {
                      var p = firstBlock.getParent();
                      if (p && p.insertBefore)
                        p.insertBefore(wrapper, firstBlock);
                      else ed.insertElement(wrapper);
                    }
                    // move blocks into wrapper
                    var node = firstBlock;
                    var afterLast = lastBlock.getNext();
                    while (node && node !== afterLast) {
                      var nxt = node.getNext();
                      wrapper.append(node);
                      node = nxt;
                    }
                  } catch (e3) {
                    // give up gracefully
                    console.error('Failed to wrap blocks for loop', e3);
                  }
                }
              }

              ed.showNotification('Loop applied', 'success');
            }, // exec
          }); // addCommand

          // Add toolbar button
          editor.ui.addButton('MyCustomButton', {
            label: 'Applicant Loop',
            command: 'toggleLoop',
            icon: `${process.env.PUBLIC_URL}/assests/img/LoopApplicant.svg`,
          });

          // Keep button pressed state in sync
          editor.on('selectionChange', function () {
            var sel = editor.getSelection();
            if (!sel) return;
            var startEl = sel.getStartElement && sel.getStartElement();
            var loopAncestor =
              startEl &&
              startEl.getAscendant(function (n) {
                return n.hasAttribute && n.hasAttribute('data-loop');
              }, true);

            if (loopAncestor) {
              editor
                .getCommand('toggleLoop')
                .setState(window.CKEDITOR.TRISTATE_ON);
            } else {
              editor
                .getCommand('toggleLoop')
                .setState(window.CKEDITOR.TRISTATE_OFF);
            }
          });

          // Contents CSS (visualize loop regions)
          editor.addContentsCss &&
            editor.addContentsCss(
              'data:text/css,' +
                encodeURIComponent(`
                [data-loop="looping"] {
                  outline: 1px dashed #7F7CFF;
                  outline-offset: 2px;
                  position: relative;
                }
                tr[data-loop] td:first-child::before, .loop-marker::before {
                  content: "looping";
                  font-size: 10px;
                  color: #7F7CFF;
                  background: #F3F0FF;
                  position: absolute;
                  top: -11px;
                  left: -1px;
                  padding: 0 4px;
                  border-radius: 3px;
                }
                tr[data-loop] td:first-child {
                  position: relative;
                }
              `)
            );
        }, // init
      }); // plugins.add
    },
    [enableCustomButton]
  );

  const config = useMemo(() => {
    return {
      extraPlugins: enableCustomButton ? 'mycustomplugin' : '',
      toolbar: enableCustomButton
        ? [...editorConfig.toolbar]
        : editorConfig.toolbar.filter((group) => group.name !== 'mytools'),
      allowedContent: true,
      extraAllowedContent:
        'p[data-loop];' +
        'div[data-loop];' +
        'li[data-loop];' +
        'tr[data-loop];' +
        'h1[data-loop];' +
        'h2[data-loop];' +
        'h3[data-loop];' +
        'h4[data-loop];' +
        'h5[data-loop];' +
        'h6[data-loop];',
    };
  }, [enableCustomButton]);

  const handleInstanceReady = (evt) => {
    editorRef.current = evt.editor;
    setIsEditorReady(true);

    if (props.data) {
      evt.editor.setData(props.data);
    }
  };

  useEffect(() => {
    if (isEditorReady && props.data && editorRef.current) {
      const currentData = editorRef.current.getData()?.replace(/"/g, "'") || '';
      if (currentData !== props.data) {
        editorRef.current.setData(props.data);
      }
    }
  }, [props.data, isEditorReady]);

  const onEditorChange = (evt) => {
    let updateData = evt.editor.getData();
    if (updateData?.trim() === '<p>undefined</p>') {
      updateData = '<p></p>';
    }
    const value = updateData?.replace(/"/g, "'") || '';
    const obj = { data: value, initData: props.initRawData };
    props.onUpdatehandler(obj);
  };

  return (
    <CKEditor
      key={enableCustomButton ? 'editor-with-custom' : 'editor-default'}
      onInstanceReady={handleInstanceReady}
      readOnly={props.isReadonly}
      config={config}
      onChange={onEditorChange}
      onBlur={(evt) => {
        if (props.onBlur) {
          props.onBlur({
            target: {
              name: props.name,
              value: evt.editor.getData(),
            },
          });
        }
      }}
    />
  );
};

export default React.memo(CkEditor);
